import { ClipboardCheck, Plus, Printer } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useReactToPrint } from 'react-to-print'
import { useDirecionadaAllList } from '@/api/queries/direcionada/direcionada-queries'
import { usePeople } from '@/api/queries/people-queries'
import kripLogo from '@/assets/KAEFER_Logo_www_pos.svg'
import {
	InspectionDetailsSection,
	ResponsiveFormModal,
} from '@/components/commons'
import { DetailField } from '@/components/commons/detail-field'
import {
	INSPECTION_COLORS,
	InspectionCard,
	InspectionCardHeader,
	InspectionDetailsHeader,
	InspectionListFilters,
	InspectionPhotoField,
} from '@/components/inspections'
import {
	InspectionSummaryBadge,
	InspectionSummaryCard,
	InspectionSummaryCardBody,
	InspectionSummaryCardField,
	InspectionSummaryCardHeader,
	InspectionSummaryCardLoading,
	InspectionSummaryCardTitle,
} from '@/components/inspections/inspection-summary-card'
import { Button } from '@/components/ui/button'
import { CardContent, CardFooter } from '@/components/ui/card'
import { Field, FieldContent, FieldLabel } from '@/components/ui/field'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { ROUTES } from '@/constants/routes'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useAuthStore, useProfileStore } from '@/store'
import type {
	AnswersOutput,
	DirecionadaAllListParams,
	DirecionadaOutput,
} from '@/types/direcionada'
import { capitalize } from '@/utils/capitalize'
import { formatDateToDisplay } from '@/utils/formatDate'

const getAnswerLabel = (answer: string, t: (key: string) => string) => {
	switch (answer) {
		case 'COMPLIANT':
			return t('inspections.directed.answerCompliant')
		case 'NON_COMPLIANT':
			return t('inspections.directed.answerDoesNotMeet')
		case 'NOT_APPLICABLE':
			return t('inspections.directed.answerNotApplicable')
		default:
			return answer
	}
}

const getStatusLabel = (
	status: string | null | undefined,
	t: (key: string) => string,
) => {
	if (!status) return '-'
	if (status === 'IN_PROGRESS') {
		return t('inspections.directed.statusInProgress')
	}
	if (status === 'RESOLVED') {
		return t('inspections.directed.statusResolved')
	}
	return status
}

const formatOptionalDate = (value?: Date | string | null) => {
	if (!value) return '-'
	return formatDateToDisplay(String(value))
}

const directedStyle = {
	accentClassName: 'border-l-kaefer-ocean-blue',
	badgeClassName:
		'border-kaefer-ocean-blue/30 bg-kaefer-ocean-blue/15 text-kaefer-ocean-blue',
	badgeLabelKey: 'inspections.types.directed.title',
} as const

const AnswerDetails = ({
	answer,
	responsibleName,
	isFreeText,
}: {
	answer: AnswersOutput
	responsibleName: string
	isFreeText: boolean
}) => {
	const { t } = useTranslation()
	const answerLabel = getAnswerLabel(answer.answer, t)

	return (
		<div className="rounded-lg border border-dashed border-kaefer-light-silver bg-white p-3">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<span className="text-sm font-semibold text-kaefer-dark-grey">
					{answer.question}
				</span>
				{!isFreeText && (
					<span className="rounded-full bg-kaefer-ocean-blue/10 px-2 py-0.5 text-xs font-semibold text-kaefer-ocean-blue">
						{answerLabel}
					</span>
				)}
			</div>

			{isFreeText && (
				<p className="mt-2 whitespace-pre-wrap break-words text-sm text-kaefer-dark-grey">
					{answer.textAnswer || '-'}
				</p>
			)}

			{!isFreeText && answer.answer === 'NON_COMPLIANT' && (
				<div className="mt-3 grid gap-3 sm:grid-cols-2">
					<DetailField
						label={t('inspections.directed.responsible')}
						value={responsibleName}
					/>
					<DetailField
						label={t('inspections.directed.deadline')}
						value={formatOptionalDate(answer.dueDate)}
					/>
					<DetailField
						label={t('inspections.directed.action')}
						value={answer.actionPlan || '-'}
					/>
					<DetailField
						label={t('inspections.directed.status')}
						value={getStatusLabel(answer.status, t)}
					/>
				</div>
			)}

			{answer.imagePath && (
				<div className="mt-3 border-t border-kaefer-light-silver/60 pt-3">
					<p className="mb-2 text-xs leading-tight text-muted-foreground">
						{t('inspections.directed.answerPhoto')}:
					</p>
					<InspectionPhotoField
						attachLabel=""
						altText={t('inspections.directed.answerImageSelected')}
						preview={answer.imagePath}
						onFileChange={() => {}}
						onRemove={() => {}}
						colors={INSPECTION_COLORS.oceanBlue}
						showRemoveButton={false}
						showPickerWhenEmpty={false}
						hideOnPreviewError
					/>
				</div>
			)}
		</div>
	)
}

const DirectedInspectionDetails = ({
	inspection,
	responsibleNames,
}: {
	inspection: DirecionadaOutput
	responsibleNames: Record<string, string>
}) => {
	const { t } = useTranslation()
	const printRef = useRef<HTMLDivElement>(null)
	const handlePrint = useReactToPrint({
		contentRef: printRef,
		documentTitle: `Inspeção Dirigida - ${inspection.quizTitle}`,
		pageStyle: `
			@page {
				size: A4;
				margin: 16mm 20mm;
			}
			@media print {
				html, body {
					margin: 0 !important;
					padding: 0 !important;
				}
			}
		`,
	})
	const headerTitle = (
		<DetailField
			label={t('inspections.common.questionnaire')}
			value={inspection.quizTitle}
		/>
	)

	return (
		<div className="space-y-5 py-2">
			<div className="flex items-start justify-between gap-2">
				<div className="flex-1">
					<InspectionDetailsHeader
						title={headerTitle}
						date={formatDateToDisplay(inspection.date)}
						createdBy={capitalize(inspection.createdByName)}
					/>
				</div>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							variant="outline"
							size="sm"
							className="shrink-0 gap-1.5 print:hidden"
							onClick={() => handlePrint()}
						>
							<Printer className="size-4" />
							Baixar PDF
						</Button>
					</TooltipTrigger>
					<TooltipContent>Exportar como PDF</TooltipContent>
				</Tooltip>
			</div>

			<div ref={printRef} className="space-y-5 print:p-7">
				{/* Cabeçalho visível apenas na impressão/PDF */}
				<div className="hidden items-center border-b border-kaefer-light-silver pb-4 print:flex">
					<img alt="Logo KRIP" className="h-10 w-auto" src={kripLogo} />
					<h1 className="flex-1 text-center font-semibold text-base text-kaefer-dark-grey">
						Relatório de Inspeção Direcionada
					</h1>
					{/* Placeholder para centralizar o título */}
					<div className="h-10 w-auto opacity-0" aria-hidden="true">
						<img alt="" className="h-10 w-auto" src={kripLogo} />
					</div>
				</div>

				<div className="hidden print:block">
					<InspectionDetailsHeader
						title={headerTitle}
						date={formatDateToDisplay(inspection.date)}
						createdBy={capitalize(inspection.createdByName)}
					/>
				</div>

				<InspectionDetailsSection>
					<DetailField
						label={t('inspections.common.contract')}
						value={capitalize(inspection.crName)}
					/>
					{inspection.customer.id && (
						<DetailField
							label={t('inspections.common.customer')}
							value={capitalize(inspection.customer.name)}
						/>
					)}
					{inspection.comment && (
						<DetailField
							label={t('inspections.directed.comment')}
							value={inspection.comment}
						/>
					)}
				</InspectionDetailsSection>

				<Field>
					<FieldLabel>{t('inspections.directed.answersTitle')}</FieldLabel>
					<FieldContent>
						<div className="space-y-3">
							{inspection.answers.map((answer) => (
								<AnswerDetails
									key={answer.id}
									answer={answer}
									isFreeText={inspection.quizKind === 'FREE_TEXT'}
									responsibleName={capitalize(
										answer.responsibleId
											? responsibleNames[answer.responsibleId] ||
													answer.responsibleId
											: '-',
									)}
								/>
							))}
						</div>
					</FieldContent>
				</Field>
			</div>
		</div>
	)
}

export const DirectedInspectionListPage = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const { data: employeeData } = usePeople(true)
	const userRole = useAuthStore((s) => s.user?.role)
	const { defaultBranchId } = useProfileStore()

	// Determina se usuário pode gerenciar filtros de branch
	const canManageBranchFilter =
		userRole === 'ADMIN' ||
		userRole === 'MANAGER' ||
		userRole === 'SSMA_MANAGER'

	const [selectedBranchId, setSelectedBranchId] = useState<string | undefined>()
	const [selectedCrId, setSelectedCrId] = useState<string | undefined>()
	const [selectedQuizId, setSelectedQuizId] = useState<string | undefined>()
	const [selectedCustomerId, setSelectedCustomerId] = useState<
		string | undefined
	>()
	const [search, setSearch] = useState('')
	const { debouncedValue: debouncedSearch, isPending: isSearchPending } =
		useDebouncedValue(search, 600)
	const [selectedInspection, setSelectedInspection] =
		useState<DirecionadaOutput | null>(null)
	const [detailsOpen, setDetailsOpen] = useState(false)
	const [cursor, setCursor] = useState<string | undefined>()
	const [inspections, setInspections] = useState<DirecionadaOutput[]>([])
	const [facets, setFacets] = useState<{
		branches: Array<{ id: string; name: string; count: number }>
		crs: Array<{ id: string; name: string; count: number }>
		quizzes: Array<{ id: string; name: string; count: number }>
		customers: Array<{ id: string; name: string; count: number }>
	}>({
		branches: [],
		crs: [],
		quizzes: [],
		customers: [],
	})
	const [pageInfo, setPageInfo] = useState<{
		nextCursor: string | null
		hasNext: boolean
	}>({
		nextCursor: null,
		hasNext: false,
	})

	const shouldShowCustomerFilter =
		facets.customers.length > 0 || Boolean(selectedCustomerId)

	const queryParamsForAllDirecionada = useMemo<DirecionadaAllListParams>(
		() => ({
			// Para usuários não-admin, usa automaticamente a branch do perfil.
			// `|| undefined` evita enviar branchId= vazio (backend responde 400).
			branchId:
				(canManageBranchFilter ? selectedBranchId : defaultBranchId) ||
				undefined,
			crId: selectedCrId,
			quizId: selectedQuizId,
			customerId: shouldShowCustomerFilter ? selectedCustomerId : undefined,
			q: debouncedSearch.trim() || undefined,
			cursor,
		}),
		[
			canManageBranchFilter,
			selectedBranchId,
			defaultBranchId,
			selectedCrId,
			selectedQuizId,
			selectedCustomerId,
			shouldShowCustomerFilter,
			debouncedSearch,
			cursor,
		],
	)

	const { data: listData, isFetching } = useDirecionadaAllList(
		queryParamsForAllDirecionada,
	)

	useEffect(() => {
		if (!listData) return

		setFacets({
			branches: listData.facets.branches ?? [],
			crs: listData.facets.crs ?? [],
			quizzes: listData.facets.quizzes ?? [],
			customers: listData.facets.customers ?? [],
		})
		setPageInfo(listData.pageInfo)

		setInspections((previous) => {
			if (!cursor) {
				return listData.items
			}

			const existingIds = new Set(previous.map((inspection) => inspection.id))
			const newItems = listData.items.filter(
				(inspection: DirecionadaOutput) => !existingIds.has(inspection.id),
			)
			return [...previous, ...newItems]
		})
	}, [listData, cursor])

	const resetListState = () => {
		setCursor(undefined)
		setPageInfo({ nextCursor: null, hasNext: false })
	}

	const handleSearchChange = (value: string) => {
		setSearch(value)
		resetListState()
	}

	const handleBranchChange = (value: string) => {
		setSelectedBranchId(value || undefined)
		setSelectedCrId(undefined)
		setSelectedCustomerId(undefined)
		resetListState()
	}

	const handleContractChange = (value: string) => {
		setSelectedCrId(value || undefined)
		setSelectedCustomerId(undefined)
		resetListState()
	}

	const handleQuizChange = (value: string) => {
		setSelectedQuizId(value || undefined)
		resetListState()
	}

	const handleCustomerChange = (value: string) => {
		setSelectedCustomerId(value || undefined)
		resetListState()
	}

	useEffect(() => {
		if (shouldShowCustomerFilter || !selectedCustomerId) return
		setSelectedCustomerId(undefined)
	}, [shouldShowCustomerFilter, selectedCustomerId])

	const handleBack = () => {
		navigate(ROUTES.INSPECTIONS.INDEX)
	}

	const handleNewInspection = () => {
		navigate(ROUTES.INSPECTIONS.DIRECTED.NEW)
	}

	const handleViewInspection = (inspection: DirecionadaOutput) => {
		setSelectedInspection(inspection)
		setDetailsOpen(true)
	}

	const handleLoadMore = () => {
		if (!pageInfo.hasNext || !pageInfo.nextCursor || isFetching) return
		setCursor(pageInfo.nextCursor)
	}

	const colors = INSPECTION_COLORS.oceanBlue
	const hasMore = pageInfo.hasNext && Boolean(pageInfo.nextCursor)
	const isLoadingMore = isFetching && Boolean(cursor)
	const footerCountLabel =
		inspections.length === 1
			? t('inspections.directed.countSingular')
			: t('inspections.directed.countPlural')
	const footerText = `${hasMore ? `${inspections.length}+` : inspections.length} ${footerCountLabel}`
	const filterSelects = [
		// Filtro de Branch: visível apenas para ADMIN, MANAGER e SSMA_MANAGER
		...(canManageBranchFilter
			? [
					{
						id: 'directed-filter-branch',
						label: t('inspections.common.branch'),
						value: selectedBranchId,
						onChange: handleBranchChange,
						options: facets.branches.map((branch) => ({
							value: branch.id,
							label: branch.name,
							count: branch.count,
						})),
						allLabel: t('inspections.directed.filterAll'),
					},
				]
			: []),
		{
			id: 'directed-filter-contract',
			label:
				t('inspections.directed.filterByContract') ??
				t('inspections.common.contract'),
			value: selectedCrId,
			onChange: handleContractChange,
			options: facets.crs.map((contract) => ({
				value: contract.id,
				label: contract.name,
				count: contract.count,
			})),
			allLabel: t('inspections.directed.filterAll'),
		},
		{
			id: 'directed-filter-quiz',
			label: t('inspections.directed.filterByQuestionnaire'),
			value: selectedQuizId,
			onChange: handleQuizChange,
			options: facets.quizzes.map((quiz) => ({
				value: quiz.id,
				label: quiz.name,
				count: quiz.count,
			})),
			allLabel: t('inspections.directed.filterAll'),
		},
		...(shouldShowCustomerFilter
			? [
					{
						id: 'directed-filter-customer',
						label: t('inspections.directed.filterByCustomer'),
						value: selectedCustomerId,
						onChange: handleCustomerChange,
						options: facets.customers.map((customer) => ({
							value: customer.id,
							label: customer.name,
							count: customer.count,
						})),
						allLabel: t('inspections.directed.filterAll'),
					},
				]
			: []),
	]

	const responsibleNames = useMemo(() => {
		if (!employeeData) return {}
		return employeeData.reduce<Record<string, string>>((acc, person) => {
			acc[person.id] = person.name
			return acc
		}, {})
	}, [employeeData])

	const renderDirectedCard = (inspection: DirecionadaOutput) => (
		<InspectionSummaryCard
			key={inspection.id}
			accentClassName={directedStyle.accentClassName}
			onClick={() => handleViewInspection(inspection)}
		>
			<InspectionSummaryCardBody>
				<InspectionSummaryCardHeader
					badge={
						<InspectionSummaryBadge
							label={t(directedStyle.badgeLabelKey)}
							className={directedStyle.badgeClassName}
						/>
					}
					topMeta={
						<span className="text-xs text-muted-foreground">
							{formatDateToDisplay(inspection.date)}
						</span>
					}
				/>

				<InspectionSummaryCardTitle>
					{capitalize(inspection.quizTitle)}
				</InspectionSummaryCardTitle>

				<div className="flex items-center justify-between gap-2 text-xs">
					<div className="min-w-0 flex-1 space-y-2">
						<InspectionSummaryCardField
							label="Criado por: "
							value={capitalize(inspection.createdByName)}
						/>
						<InspectionSummaryCardField
							label="Contrato: "
							value={capitalize(inspection.crName)}
						/>
					</div>
				</div>
			</InspectionSummaryCardBody>
		</InspectionSummaryCard>
	)

	return (
		<>
			<InspectionCard colors={colors}>
				<InspectionCardHeader
					icon={<ClipboardCheck className="size-5" />}
					title={t('inspections.types.directed.title')}
					subtitle={t('inspections.directed.listSubtitle')}
					colors={colors}
					onBack={handleBack}
					actions={
						<Button
							onClick={handleNewInspection}
							className="w-full gap-2 bg-kaefer-ocean-blue font-semibold text-white hover:bg-kaefer-ocean-blue/90 sm:w-auto"
						>
							<Plus className="size-4" />
							{t('inspections.directed.newInspection')}
						</Button>
					}
				/>
				<CardContent className="flex-1 space-y-4">
					<InspectionListFilters
						searchValue={search}
						onSearchChange={handleSearchChange}
						selects={filterSelects}
					/>

					{isSearchPending ? (
						<InspectionSummaryCardLoading
							accentClassName={directedStyle.accentClassName}
						/>
					) : inspections.length > 0 ? (
						<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
							{inspections.map((inspection) => renderDirectedCard(inspection))}
						</div>
					) : (
						<div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-kaefer-light-silver py-16">
							<ClipboardCheck className="size-10 text-kaefer-silver" />
							<p className="mt-3 text-sm text-muted-foreground">
								{t('inspections.directed.emptyList')}
							</p>
						</div>
					)}

					{hasMore && inspections.length > 0 && (
						<div className="flex justify-center">
							<Button
								variant="outline"
								onClick={handleLoadMore}
								disabled={isLoadingMore}
								className="gap-2"
							>
								{isLoadingMore ? (
									<>
										<div className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
										{t('inspections.directed.loadingMore')}
									</>
								) : (
									<>
										<Plus className="size-4" />
										{t('inspections.directed.loadMore')}
									</>
								)}
							</Button>
						</div>
					)}
				</CardContent>
				<CardFooter className="mt-4 border-t border-kaefer-ocean-blue/30 bg-kaefer-ocean-blue/5 py-3">
					<p className="text-xs text-muted-foreground w-full">{footerText}</p>
				</CardFooter>
			</InspectionCard>
			<ResponsiveFormModal
				open={detailsOpen}
				onOpenChange={setDetailsOpen}
				title={t('inspections.directed.details')}
				dialogClassName="max-h-[85vh] overflow-y-auto sm:max-w-4xl"
			>
				{selectedInspection && (
					<DirectedInspectionDetails
						inspection={selectedInspection}
						responsibleNames={responsibleNames}
					/>
				)}
			</ResponsiveFormModal>
		</>
	)
}
